// @ts-nocheck
/**
 * This module is responsible for blocking trackers on a given page. It uses a
 * mutation observer to monitor the document for changes and block any new
 * trackers that are added. It also checks the initial state of the document for
 * any trackers that may have been added before the module was loaded.
 *
 * @module trackingBlocker
 */

import store from '@/store';
import { getCookies } from '@/utilities/cookies';
import { handleError, getInlineNodeSource } from '@/utilities';
import Events from '@/constants/events';

/**
 * Check if the category is allowed based on the consent state. If the blocking
 * mode is 'discovery' or 'disabled', all categories are allowed. If the
 * blocking mode is 'strict', only 'strictly_necessary' and 'ignored' are
 * allowed. If the blocking mode is 'permissive', 'strictly_necessary',
 * 'unclassified', and 'ignored' are allowed. If the category is in the consent
 * state and the state is 'accepted', the category is allowed.
 *
 * @param {string} category
 * @returns {boolean}
 */
function isAllowedCategory(category) {
  const {
    config: {
      regionTemplate: {
        settings: { consent: { blockingMode: mode = 'disabled' } = {} } = {},
      } = {},
    } = {},
  } = store;
  if (mode === 'discovery' || mode === 'disabled') {
    return true;
  }
  if (mode === 'strict') {
    if (category === 'strictly_necessary' || category === 'ignored') {
      return true;
    }
  }
  if (mode === 'permissive') {
    if (
      category === 'strictly_necessary' ||
      category === 'unclassified' ||
      category === 'ignored'
    ) {
      return true;
    }
  }

  /* Check if the category is in the consent state and if the state is 'accepted'. */
  return store.consentState.allowState[category] === true;
}

const localhostRegex =
  /^(http)?s?:?\/\/(.*\.)?(localhost|127\.0\.0\.1):?\d{0,6}/;

/* Regex for concord site-v1 domains. Other concord domains are not blocked
by default as they should be managed via Concord. */
const concordRegEx =
  /^(http)?s?:?\/\/(.*\.)?concord\.(tech|network|work):?\d{0,5}\/site-v1/;

const extensionRegEx =
  /^(chrome-extension|moz-extension|safari-extension|ms-browser-extension):/;

/**
 * Check if the src should be blocked. If the src is a localhost domain, a
 * concord site-v1 domain, a browser extension, or a local path, the src should
 * not be blocked. Otherwise, the src should be blocked.
 *
 * @param {string} src
 * @returns {boolean}
 */
function isBlockedSource(src) {
  // Check for empty/invalid sources
  if (!src || src.length === 0) {
    return false;
  }

  // Check for protocol-relative URLs
  if (src.startsWith('//')) {
    return true;
  }

  // Check allowed patterns
  if (
    src.match(localhostRegex) || // localhost domain
    src.match(concordRegEx) || // concord site-v1 domain
    src.match(extensionRegEx) || // browser extensions
    src.startsWith('/') || // local path
    src.startsWith('data:') || // data URL
    src.startsWith('about:') || // about URL
    src.endsWith('==') // base64 encoded
  ) {
    return false;
  }

  // Special case for Google Tag Manager in advanced mode
  if (
    store.gcmMode === 'advanced' &&
    src.includes('https://www.googletagmanager.com')
  ) {
    // Google advanced consent mode = no blocking of GTM
    return false;
  }

  // Block everything else by default
  return true;
}

/**
 * Check if the node is one that should be blocked. This is determined by
 * checking if the node contains the string 'script' and the function
 * 'createElement'. This is a common pattern for scripts that dynamically create
 * other scripts.
 *
 * @param {Node} node
 * @returns {boolean}
 */
function isBlockInlineNode(node) {
  const script = node.innerHTML;

  /* Check if the script contains the string 'script' and the function 'createElement'. */
  const createElementRegex = /createElement\(.+\)/;
  return script.indexOf('script') !== -1 && createElementRegex.test(script);
}

/**
 * The valid categories for a tracker.
 *
 * @type {string[]}
 */
const validCategories = [
  'malicious',
  'marketing',
  'analytics',
  'functional',
  'strictly_necessary',
  'unclassified',
  'ignored',
];

/**
 * Add a tracker to the site trackers for a given project. Works by checking if
 * the tracker is in the known trackers list, and adding it if not. Trackers are
 * added using the name and type of the tracker, with name being based on the
 * src of the tracker and type being 'script', 'iframe', or 'img'.
 *
 * @param {Node} node
 * @param {string} src
 * @param {Tracker[]} trackers
 * @returns {string} The category of the tracker.
 */
function addNodeTracker(node, src, trackers) {
  /* Check if the node is a script, iframe, img, or link and set the type to the
  appropriate value. All nodes that get to this point should have a tagName of
  'iframe', 'script', 'img', or 'link, so return 'ignored' if not. Ignored nodes are allowed. */
  let type;
  if (node.tagName === 'SCRIPT') {
    type = 'script';
  } else if (node.tagName === 'IFRAME') {
    type = 'iframe';
  } else if (node.tagName === 'IMG') {
    type = 'image';
  } else if (node.tagName === 'LINK') {
    type = 'link';
  } else {
    return 'ignored';
  }

  // Strip query parameters and fragments from the src URL to keep it clean.
  const cleanSrc = src.split('#')[0].split('?')[0];

  /* Check if the tracker is in the trackers list. 
  If it is, set the category to the category of the tracker. */
  const tracker = trackers.find(
    ({ regex, type: trackerType, category }) =>
      cleanSrc.match(regex) && trackerType === type && category,
  );

  if (tracker) {
    /* Check if the tracker has an existing data-concord-category attribute. If it does, check if it
    matches the category of the tracker. If it doesn't, log an error. */
    if (node.hasAttribute('data-concord-category')) {
      const nodeCategory = node.getAttribute('data-concord-category');
      if (tracker?.category !== nodeCategory) {
        handleError(
          `Concord category mismatch on ${tracker?.name}: ${tracker?.category} != ${nodeCategory?.value}`,
          'info',
        );
      }
    }
    node.setAttribute('data-concord-category', tracker.category);
    return tracker.category;
  }

  /* If the tracker is not in the trackers list, set the category to ignored. If the node 
  has a valid data-concord-category attribute, use that as the category. */
  let category = 'ignored';
  const nodeCategory = node.getAttribute('data-concord-category');
  if (nodeCategory && validCategories.includes(nodeCategory.value)) {
    category = nodeCategory.value;
  }
  /* Add the tracker to the site trackers and return the category. */
  store.addSiteTracker({ name: cleanSrc, type, category });
  return category;
}

/**
 * Block a node. If the node is a known tracker, add the tracker to the site
 * trackers. If the tracker is allowed, add an attribute to the node and return
 * false. If the tracker is not allowed, block the node by changing the type to
 * 'text/plain', adding a modified attribute, and returning true.
 *
 * @param {Node} node
 * @param {Tracker[]} trackers
 * @returns {boolean}
 */
function blockNode(node, trackers) {
  const src = node.getAttribute('src');
  const href = node.getAttribute('href');

  // Add a data-concord-src or data-concord-href attribute. All nodes that get here should have a src or href.
  if (src) {
    node.setAttribute('data-concord-src', src);
  } else if (href) {
    node.setAttribute('data-concord-href', href);
  }

  const category = addNodeTracker(node, src || href, trackers);

  if (isAllowedCategory(category)) {
    node.setAttribute('data-concord-modified', 'allowed');
    return false;
  }

  // Remove the original src or href attribute from the node.
  if (src) {
    node.removeAttribute('src');
  } else if (href) {
    node.removeAttribute('href');
  }

  /* Check if the node is a script. If it is, check if it has a type attribute. If it does,
  add the data-concord-type attribute to the new element and set the node type to 'text/plain' */
  if (node.tagName === 'SCRIPT') {
    if (node.hasAttribute('type')) {
      node.setAttribute('data-concord-type', node.type);
      node.setAttribute('type', 'text/plain');
    }
  }

  /* Check if the node is an iframe and if it has a display attribute. If it does,
    set the display attribute of the new element to the value of the display attribute. 
    Always hide the iframe by setting the display attribute of the new element to none. */
  if (node.tagName === 'IFRAME' || node.tagName === 'IMG') {
    if (node.style.display) {
      node.setAttribute('data-concord-display', node.style.display);
    }
    node.style.setProperty('display', 'none');
  }

  node.setAttribute('data-concord-modified', 'blocked');
  return true;
}

/**
 * Block an inline node without a src. Blocked inline nodes are nodes that
 * contain the string 'script' and the function 'createElement'. If the node is
 * blocked, add a data-concord-inline attribute and a data-concord-modified
 * attribute. Clone the node, remove it, and store the clone for later.
 *
 * @param {Node} node
 * @param {Tracker[]} trackers
 * @returns {boolean}
 */
function blockInlineNode(node, trackers) {
  /* Check the inline HTML to see if there is a src attribute and check if the node has a parent. 
    If so, add a data-concord-inline attribute. If not, return false. */
  const src = getInlineNodeSource(node);
  const { parentNode } = node;

  if (src && parentNode && isBlockedSource(src)) {
    let category = 'ignored';

    node.setAttribute('data-concord-inline', src);
    category = addNodeTracker(node, src, trackers);
    if (isAllowedCategory(category)) {
      node.setAttribute('data-concord-modified', 'allowed');
      return false;
    }

    node.setAttribute('data-concord-modified', 'blocked');

    /* Clone the node and it's parent and store them for later,
     then remove the node from the DOM. */
    const clone = {
      node: node.cloneNode(true),
      parent: parentNode,
    };

    const updatedNodes = [...store.removedNodes, clone];
    store.updateRemovedNodes(updatedNodes);
    parentNode.removeChild(node);

    return true;
  }
  return false;
}

function checkNode(node, siteTrackers) {
  if (
    !node?.attributes ||
    node.getAttribute('data-concord-modified') === 'allowed' ||
    (node.tagName !== 'SCRIPT' &&
      node.tagName !== 'IFRAME' &&
      node.tagName !== 'IMG' &&
      node.tagName !== 'LINK')
  ) {
    return false;
  }
  const { attributes } = node;
  /* Check if the node has an ignore or modified attribute. Don't do anything if it does. */
  if (attributes.getNamedItem('data-concord-ignore')) {
    return false;
  }

  /* Check if the node has a src or href (for link nodes) attribute. If it does,
  check if it is blocked source or not and if so, run through the block node process. */
  if (node.hasAttribute('src') || node.hasAttribute('href')) {
    const src = node.getAttribute('src') || node.getAttribute('href');
    if (isBlockedSource(src)) {
      return blockNode(node, siteTrackers);
    }
    /* No src. Check if it creates
       other scripts and potentially block it if so. */
  } else if (isBlockInlineNode(node)) {
    return blockInlineNode(node, siteTrackers);
  }
  return false;
}

/**
 * Get all the cookies from the document and add them to the site trackers.
 * Cookies are added to the site trackers with the name of the cookie and the
 * type 'cookie'. They are not added if they are already known.
 *
 * @returns {boolean}
 */
function addCookies() {
  const cookies = getCookies();
  cookies.forEach((cookie) => {
    store.addSiteTracker({ name: cookie.name, type: 'cookie' });
  });
  return true;
}

/* Debounce the upload and tracker dispatch to prevent 
    multiple uploads and events when each mutation is observed. */
let debounceTimer = null;
const debounceDelay = 10; // 10ms

function debouncedUpload() {
  if (window.concord?.loaded) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      store.uploadSiteTrackers();
    }, debounceDelay);
  }
}

/* Scan for new cookies and upload them. */
function handleCookiesAndUpload() {
  /* Get all the cookies from the document and add 
      them if they aren't in the known trackers list. */
  if (addCookies()) {
    debouncedUpload();
  }
}

/**
 * Set the known trackers, check the initial state of the document for any
 * trackers, and create a new mutation observer to monitor the document for
 * changes. If there are any changes, upload the site trackers and apply the
 * tracker consent.
 */
export function blockAllTrackers() {
  const trackers = store.getKnownTrackers();
  /* Img and link domains that should be processed. All others are ignored by default. */
  const imgLinkDomains = [
    'googleusercontent',
    'gstatic',
    'googleapis',
    'analytics.twitter',
  ];

  function shouldProcessNode(node, domains) {
    if (node.tagName === 'IMG' && !node.src) {
      return false;
    }
    if (node.tagName === 'LINK' && !node.href) {
      return false;
    }
    if (node.tagName === 'IMG' && node.src) {
      return domains.some((domain) => node.src.includes(domain));
    }
    if (node.tagName === 'LINK' && node.href) {
      return domains.some((domain) => node.href.includes(domain));
    }
    return true;
  }

  try {
    /* Check the initial state of the document for any trackers 
    that may have been added before we got here. */
    document.querySelectorAll('iframe, script, img, link').forEach((node) => {
      if (!shouldProcessNode(node, imgLinkDomains)) {
        return;
      }
      if (node.src && node.src.includes('https://www.googletagmanager.com')) {
        // eslint-disable-next-line no-console
        console.log('Google Tags detected prior to consent.');
      }
      checkNode(node, trackers);
    });

    /* Create a new observer to monitor the document for changes */
    const observer = new MutationObserver((mutations) => {
      let modified = false;

      mutations.forEach(({ type, addedNodes }) => {
        /* Check if the type of mutation is a child list change, 
        where children of a node were added. If the added node is an element 
        node and is an iframe, script, or image, check the node. */
        if (type === 'childList') {
          addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              /* Short-circuit if node.tagName is IMG or LINK and isn't a specific checked domain */
              if (!shouldProcessNode(node, imgLinkDomains)) {
                return;
              }
              modified = checkNode(node, trackers);
              const children = Array.from(
                node.querySelectorAll('iframe, script, img'),
              );
              if (children) {
                /* if the node has pre-existing children check them. */
                modified =
                  children.map((n) => {
                    /* Short-circuit if child node.tagName is IMG or LINK and it isn't a different domain */
                    if (!shouldProcessNode(n, imgLinkDomains)) {
                      return;
                    }
                    return checkNode(n, trackers);
                  }) || modified;
              }
            }
          });
        }

        if (modified && modified.length > 0) {
          /* Rescan the document for newly added cookies */
          addCookies();
          debouncedUpload();
        }
      });
    });
    /* Starts the monitoring */
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });

    /* Check for and process cookies on page load */
    handleCookiesAndUpload();
    /* Check for and process cookies on consent state change */
    window.addEventListener(Events.ConsentStateChanged, handleCookiesAndUpload);
  } catch (err) {
    handleError(err);
  }
}
